import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  Input,
  NgModule,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  tap,
} from 'rxjs';
import {
  AutoScrollToDirectiveModule,
  RouterStaticLinkDirectiveModule,
} from '@icilalune/utils';
import { MatIconModule } from '@angular/material/icon';
import { SvgIconComponentModule } from '@nx-icilalune/icilalune/media';
import { ComponentStore } from '@ngrx/component-store';
import {
  MainMenuItem,
  MainMenuTreeItem,
} from '@nx-icilalune/data/icilalune/types';
import { LetModule, PushModule } from '@ngrx/component';
import { Tree, TreeItem } from '@nx-icilalune/drupal-types';
import { isEqual } from '@madeinlune/ngx-operators';
import { HorizontalMenuComponent } from '@icilalune/ui/horizontal-menu';
import { SettingsLinkComponent } from '../settings-link/settings-link.component';

export const COMPONENT_MAIN_MENU_SELECTED_TREE_ITEM = new InjectionToken<
  Observable<MainMenuTreeItem>
>('COMPONENT_MAIN_MENU_SELECTED_TREE_ITEM');

export const COMPONENT_BREADCRUMBS = new InjectionToken<
  Observable<MainMenuItem[]>
>('COMPONENT_BREADCRUMBS');

export const COMPONENT_MAIN_MENU_TREE = new InjectionToken<
  Observable<Tree<MainMenuTreeItem>>
>('COMPONENT_MAIN_MENU_TREE');

interface MainMenuComponentStore {
  firstLevelItems: TreeItem<any>[];
  secondLevelItems: TreeItem<any>[];
  forceFirstLevelDisplay?: boolean;
  userActive: boolean;
}

@Component({
  selector: 'sh-ui-main-menu',
  template: `
    <ng-container *ngrxLet="vm$; let vm">
      <div class="b-menus">
        <nav aria-labelledby="primary-navigation">
          <sh-ui-horizontal-menu
            class="main-level"
            (mousemove)="onMainMenuMouseMove()"
            (mouseleave)="onMainMenuMouseLeave()"
            (menuScroll)="onMainMenuMouseMove()"
            [class.first-level-only]="firstLevelOnly$ | ngrxPush"
            [breadcrumbs]="breadcrumbs$ | ngrxPush"
            [menuItems]="vm.firstLevelItems"
          >
            <sh-ui-settings-link settingsLink></sh-ui-settings-link>
          </sh-ui-horizontal-menu>
        </nav>
        <ng-container *ngIf="vm.secondLevelItems as secondLevelItems">
          <ng-container *ngIf="vm.secondLevelItems.length > 0">
            <sh-ui-horizontal-menu
              class="second-level"
              [openMenuTemplate]="openMenuTemplate"
              [breadcrumbs]="breadcrumbs$ | ngrxPush"
              [menuItems]="vm.secondLevelItems"
            >
            </sh-ui-horizontal-menu>
          </ng-container>
        </ng-container>
      </div>
    </ng-container>

    <ng-template #openMenuTemplate>
      <ng-container *ngrxLet="firstBreadcrumbItem$; let firstBreadcrumbItem">
        <button
          class="open-menu-item lune-btn"
          [ngClass]="firstBreadcrumbItem.color"
          [style.--themeColor]="
            'var(--lune-color-section-' +
            firstBreadcrumbItem.color +
            '-default)'
          "
          [style.--themeColor-contrast]="
            'var(--lune-color-section-' +
            firstBreadcrumbItem.color +
            '-contrast)'
          "
          [style.--themeColor-light]="
            'var(--lune-color-section-' + firstBreadcrumbItem.color + '-light)'
          "
          (click)="onOpenFirstLevelMenu()"
        >
          <i2l-svg-icon icon="look-up"></i2l-svg-icon>
          <span [innerHTML]="firstBreadcrumbItem.title"></span>
        </button>
      </ng-container>
    </ng-template>
  `,
  styleUrls: ['./main-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainMenuComponent extends ComponentStore<MainMenuComponentStore> {
  readonly vm$ = this.select((s) => s);

  readonly forceFirstLevelDisplay$ = this.select(
    (s) => s.forceFirstLevelDisplay
  );

  firstBreadcrumbItem$ = this.breadcrumbs$.pipe(
    tap(() => {
      this.patchState({
        forceFirstLevelDisplay: false,
      });
    }),
    filter((breadcrumbs) => breadcrumbs?.length > 0),
    map((breadcrumbs) => breadcrumbs[0])
  );

  firstLevelOnly$: Observable<boolean> = this.breadcrumbs$.pipe(
    filter((breadcrumbs) => !!breadcrumbs),
    map((breadcrumbs) => {
      const secondItem: MainMenuItem | undefined = breadcrumbs?.[1];
      return (
        breadcrumbs.length === 1 ||
        (breadcrumbs.length === 2 &&
          (secondItem?.color === 'blog' ||
            secondItem?.color === 'actualites' ||
            secondItem?.color === 'clients'))
      );
    }),
    tap((firstLevelOnly) => {
      if (firstLevelOnly) {
        this.el.nativeElement.classList.remove('second-level');
      } else {
        this.el.nativeElement.classList.add('second-level');
      }
    })
  );

  @Input()
  menuItems!: MainMenuTreeItem[] | undefined;

  setForceFirstLevelDisplay = this.effect(
    (forceFirstLevelDisplay$: Observable<boolean | undefined>) => {
      return forceFirstLevelDisplay$.pipe(
        tap((forceFirstLevelDisplay) => {
          if (forceFirstLevelDisplay) {
            this.el.nativeElement.classList.add('force-first-level-display');
          } else {
            this.el.nativeElement.classList.remove('force-first-level-display');
          }
          this.patchState({
            forceFirstLevelDisplay,
            userActive: forceFirstLevelDisplay,
          });
        }),
        debounceTime(3000),
        tap(() => {
          this.patchState({
            forceFirstLevelDisplay: false,
          });
        })
      );
    }
  );

  constructor(
    @Inject(COMPONENT_MAIN_MENU_TREE)
    public mainMenu$: Observable<Tree<MainMenuTreeItem>>,
    @Inject(COMPONENT_MAIN_MENU_SELECTED_TREE_ITEM)
    public selectedMainMenuItem$: Observable<MainMenuTreeItem>,
    @Inject(COMPONENT_BREADCRUMBS)
    protected breadcrumbs$: Observable<MainMenuItem[]>,
    private el: ElementRef<HTMLElement>
  ) {
    super({ firstLevelItems: [], secondLevelItems: [], userActive: false });
    this.setForceFirstLevelDisplay(this.forceFirstLevelDisplay$);
    this.patchState(
      this.mainMenu$.pipe(
        map((mainMenu) => {
          return { firstLevelItems: mainMenu.items };
        })
      )
    );
    this.patchState(
      combineLatest([this.breadcrumbs$, this.mainMenu$]).pipe(
        filter(
          ([breadcrumbs, mainMenu]) =>
            !!breadcrumbs && !!mainMenu && breadcrumbs.length >= 2
        ),
        isEqual(),
        map(([breadcrumbs, mainMenu]) => {
          const firstBreadcrumb = breadcrumbs[0];
          const menuItem = mainMenu.items.find(
            (i) => i['id'] === firstBreadcrumb.id
          );
          return {
            secondLevelItems:
              menuItem?.items && menuItem.items.length ? menuItem.items : [],
          };
        })
      )
    );
  }

  onOpenFirstLevelMenu() {
    this.patchState({ forceFirstLevelDisplay: true });
  }

  onMainMenuMouseMove() {
    this.patchState({ forceFirstLevelDisplay: true });
  }

  onMainMenuMouseLeave() {
    // this.patchState({ forceFirstLevelDisplay: false });
  }
}

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    RouterStaticLinkDirectiveModule,
    MatIconModule,
    AutoScrollToDirectiveModule,
    SvgIconComponentModule,
    LetModule,
    HorizontalMenuComponent,
    PushModule,
    HorizontalMenuComponent,
    SettingsLinkComponent,
  ],
  declarations: [MainMenuComponent],
  exports: [MainMenuComponent],
})
export class MainMenuComponentModule {}
